import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Sidebar from "./components/Sidebar";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import FuzzyMatchPage from "./pages/FuzzyMatchPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import CasesListPage from "./pages/CasesListPage";
import CaseDetailPage from "./pages/CaseDetailPage";
import AuditLogPage from "./pages/AuditLogPage";
import ScreeningHubPage from "./pages/ScreeningHubPage";
import APIKeysPage from "./pages/APIKeysPage";
import SettingsPage from "./pages/SettingsPage";
import TransactionMonitoringPage from "./pages/TransactionMonitoringPage";
import STRGeneratorPage from "./pages/STRGeneratorPage";
import AdverseMediaPage from "./pages/AdverseMediaPage";
import PrecisionAdverseMediaPage from "./pages/PrecisionAdverseMediaPage";
import GSTLookupPage from "./pages/GSTLookupPage";
import ECourtsPage from "./pages/ECourtsPage";
import OpenSanctionsPage from "./pages/OpenSanctionsPage";
import MCA21Page from "./pages/MCA21Page";
import KybOneClickPage from "./pages/KybOneClickPage";
import ComplianceCopilotPage from "./pages/ComplianceCopilotPage";
import VerifyCasesPage from "./pages/VerifyCasesPage";
import VerifyConsolePage from "./pages/VerifyConsolePage";
import UnifiedSearchPage from "./pages/UnifiedSearchPage";
import EnforcementPage from "./pages/EnforcementPage";
import WatchlistPage from "./pages/WatchlistPage";
import OnboardingTokensPage from "./pages/OnboardingTokensPage";
import PublicOnboardingPage from "./pages/PublicOnboardingPage";
import WebhooksPage from "./pages/WebhooksPage";
import PANVerifyPage from "./pages/PANVerifyPage";
import FaceMatchPage from "./pages/FaceMatchPage";
import VKYCPage from "./pages/VKYCPage";
import JourneyPage from "./pages/JourneyPage";
import AadhaarVerifyPage from "./pages/AadhaarVerifyPage";
import FraudDetectPage from "./pages/FraudDetectPage";
import BehavioralPage from "./pages/BehavioralPage";
import AtoPage from "./pages/AtoPage";
import FraudNetworkPage from "./pages/FraudNetworkPage";
import CryptoScreenPage from "./pages/CryptoScreenPage";
import DigiLockerMockConsent from "./pages/DigiLockerMockConsent";
import DirectorSearchPage from "./pages/DirectorSearchPage";
import DINWatchlistPage from "./pages/DINWatchlistPage";
import QuickSearch from "./components/QuickSearch";
import DigiLockerComplete from "./pages/DigiLockerComplete";
import PrivacyDataHandlingPage from "./pages/PrivacyDataHandlingPage";
import EntityIntelligencePage from "./pages/EntityIntelligencePage";
import ReportsPage from "./pages/ReportsPage";
import CKYCPage from "./pages/CKYCPage";
import LivenessPage from "./pages/LivenessPage";
import CbillPage from "./pages/CbillPage";
import SystemHealthPage from "./pages/SystemHealthPage";
import EntityScreeningPage from "./pages/EntityScreeningPage";
import IndianEntityPage from "./pages/IndianEntityPage";
import CompanyVerifyPage from "./pages/CompanyVerifyPage";
import { QuickScreenButton } from "./components/QuickScreenButton";
import { DemoMode } from "./components/DemoMode";
import { DemoDataBadge } from "./components/DemoDataBadge";
import "@/App.css";

// Protected Route wrapper
// Optional `basicAllowed` flag — when a Basic-plan user visits a route that
// is NOT in the Basic allowlist, redirect them to /dashboard so they can't
// URL-hop past the sidebar gating.
const BASIC_ALLOWED = new Set([
  "/dashboard", "/pan-verify", "/aadhaar-verify", "/settings", "/profile",
]);

function ProtectedRoute({ children, basicAllowed = null }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "#080c12",
        color: "#94a3b8"
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Plan gating — Basic users may only reach allowlisted paths.
  const isBasic = String(user?.plan || "").toLowerCase() === "basic";
  const allow = basicAllowed !== null
    ? basicAllowed
    : BASIC_ALLOWED.has(location.pathname);
  if (isBasic && !allow) {
    return <Navigate to="/pan-verify" replace />;
  }

  return children;
}

// Layout with Sidebar
function DashboardLayout({ children }) {
  return (
    <div style={{ display: "flex", background: "#080c12", minHeight: "100vh" }}>
      <Sidebar />
      <div className="main-content-area" style={{
        marginLeft: "220px",
        flex: 1,
        padding: "32px",
        maxWidth: "1600px",
        minWidth: 0,
      }}>
        {children}
      </div>
      <QuickScreenButton />
      <DemoMode />
      <DemoDataBadge />
    </div>
  );
}

// Placeholder pages

// CasesPage placeholder removed - using CasesListPage

// AuditLogsPage placeholder removed - using imported AuditLogPage

// SettingsPage placeholder removed - using imported SettingsPage

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <QuickSearch />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/onboarding/:token" element={<PublicOnboardingPage />} />
          <Route path="/digilocker/mock-consent" element={<DigiLockerMockConsent />} />
          <Route path="/digilocker/complete" element={<DigiLockerComplete />} />
          <Route path="/privacy/data-handling" element={<PrivacyDataHandlingPage />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CustomersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CustomerDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/screening"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ScreeningHubPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TransactionMonitoringPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/cases"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CasesListPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/cases/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CaseDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/str-generator"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <STRGeneratorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/adverse-media"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AdverseMediaPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/adverse-media/precision"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PrecisionAdverseMediaPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/gst-lookup"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <GSTLookupPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/court-records"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ECourtsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/opensanctions"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OpenSanctionsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/mca21"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MCA21Page />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/kyb/one-click"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <KybOneClickPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/copilot"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ComplianceCopilotPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/verify/cases"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <VerifyCasesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/verify/console"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <VerifyConsolePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mca21/directors"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DirectorSearchPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mca21/din-watchlist"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DINWatchlistPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/enforcement"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EnforcementPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/watchlist"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <WatchlistPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/onboarding-tokens"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OnboardingTokensPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/webhooks"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <WebhooksPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pan-verify"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PANVerifyPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/aadhaar-verify"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AadhaarVerifyPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/fraud-detect"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FraudDetectPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/behavioral"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BehavioralPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ato"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AtoPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/fraud-network"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FraudNetworkPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/crypto-screen"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CryptoScreenPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />


          <Route
            path="/face-match"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FaceMatchPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vkyc"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <VKYCPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/journey"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <JourneyPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/360-search"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <UnifiedSearchPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/entity-intelligence"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EntityIntelligencePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReportsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/developers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <APIKeysPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AuditLogPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/platform-admin"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SuperAdminPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SubscriptionPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/fuzzy-match"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FuzzyMatchPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/ckyc"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CKYCPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/liveness"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LivenessPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cbill"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CbillPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-health"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SystemHealthPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/entity-screening"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EntityScreeningPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/india-entity"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <IndianEntityPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-verify"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CompanyVerifyPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
